/**
 * CreateRoomModal — Bottom-sheet modal for creating a new lobby room.
 *
 * Fields:
 *   - Game type: Cee-Lo / Craps
 *   - Room type: Public / Private (private rooms hide from the public
 *     room list; invites happen from inside the game)
 *   - Buy-in: stepped chip ladder up to 10B; chips above the player's
 *     current balance render disabled.
 *
 * Submit emits 'submit' with the payload the server expects on
 * LOBBY_CREATE_ROOM. The screen is responsible for showing the loading
 * screen and routing into the game once MATCH_FOUND comes back.
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import type { LobbyCreateRoomPayload } from '@sdl/shared';

interface CreateRoomModalEvents {
  'submit': LobbyCreateRoomPayload;
  'close': void;
}

type GameType = 'ceelo' | 'craps';

interface BuyInOption {
  stacks: number;
  label: string;
}

const BUY_IN_LADDER: BuyInOption[] = [
  { stacks: 1_000,            label: '1K' },
  { stacks: 10_000,           label: '10K' },
  { stacks: 50_000,           label: '50K' },
  { stacks: 100_000,          label: '100K' },
  { stacks: 1_000_000,        label: '1M' },
  { stacks: 10_000_000,       label: '10M' },
  { stacks: 50_000_000,       label: '50M' },
  { stacks: 100_000_000,      label: '100M' },
  { stacks: 250_000_000,      label: '250M' },
  { stacks: 500_000_000,      label: '500M' },
  { stacks: 750_000_000,      label: '750M' },
  { stacks: 1_000_000_000,    label: '1B' },
  { stacks: 10_000_000_000,   label: '10B' },
];

export class CreateRoomModal extends EventEmitter<CreateRoomModalEvents> {
  public element: HTMLElement;
  private gameType: GameType = 'ceelo';
  private isPrivate: boolean = false;
  private buyIn: number = 100_000;  // default to 100K
  private balanceStacks: number = 0;
  private isVisible = false;
  private balanceEl: HTMLElement;
  private submitEl: HTMLButtonElement;

  constructor() {
    super();
    CreateRoomModal.injectStyles();

    this.element = document.createElement('div');
    this.element.className = 'sdl-crm-root';
    this.element.innerHTML = `
      <div class="sdl-crm-backdrop" data-backdrop></div>
      <div class="sdl-crm-sheet">
        <header class="sdl-crm-header">
          <div class="sdl-crm-title-block">
            <div class="sdl-crm-crumb">LOBBY</div>
            <div class="sdl-crm-title">CREATE ROOM</div>
          </div>
          <button class="sdl-crm-close" type="button" aria-label="Close" data-close>✕</button>
        </header>

        <div class="sdl-crm-body">
          <section class="sdl-crm-section">
            <div class="sdl-crm-section-label">GAME TYPE</div>
            <div class="sdl-crm-row" data-mode-row>
              <button type="button" class="sdl-crm-card sdl-crm-active" data-mode="ceelo">
                <span class="sdl-crm-card-icon">🎲</span>
                <span class="sdl-crm-card-title">CEE-LO</span>
              </button>
              <button type="button" class="sdl-crm-card" data-mode="craps">
                <span class="sdl-crm-card-icon">💰</span>
                <span class="sdl-crm-card-title">CRAPS</span>
              </button>
            </div>
          </section>

          <section class="sdl-crm-section">
            <div class="sdl-crm-section-label">ROOM TYPE</div>
            <div class="sdl-crm-row" data-priv-row>
              <button type="button" class="sdl-crm-pill sdl-crm-active" data-priv="public">PUBLIC</button>
              <button type="button" class="sdl-crm-pill" data-priv="private">PRIVATE</button>
            </div>
            <div class="sdl-crm-hint" data-priv-hint>Listed in the public room list.</div>
          </section>

          <section class="sdl-crm-section">
            <div class="sdl-crm-section-label">BUY-IN</div>
            <div class="sdl-crm-chips" data-buyin-row></div>
            <div class="sdl-crm-balance">
              YOUR BALANCE: <span class="sdl-crm-balance-value" data-balance>—</span>
            </div>
          </section>
        </div>

        <div class="sdl-crm-footer">
          <button class="sdl-crm-submit" type="button" data-submit>
            CREATE ROOM
            <span class="sdl-crm-submit-arrow">›</span>
          </button>
        </div>
      </div>
    `;

    this.balanceEl = this.element.querySelector('[data-balance]') as HTMLElement;
    this.submitEl = this.element.querySelector('[data-submit]') as HTMLButtonElement;

    this.element.querySelector('[data-backdrop]')!.addEventListener('click', () => this.fireClose());
    this.element.querySelector('[data-close]')!.addEventListener('click', () => this.fireClose());

    this.element.querySelector('[data-mode-row]')!.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-mode]');
      if (!btn || btn.hasAttribute('disabled')) return;
      this.setGameType(btn.dataset.mode as GameType);
    });
    this.element.querySelector('[data-priv-row]')!.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-priv]');
      if (!btn) return;
      this.setPrivacy(btn.dataset.priv === 'private');
    });

    this.renderChips();
    this.submitEl.addEventListener('click', () => this.fireSubmit());
  }

  show(opts: { balanceStacks: number }): void {
    this.balanceStacks = opts.balanceStacks;
    this.balanceEl.textContent = `💵 ${formatStacks(this.balanceStacks)}`;
    this.renderChips();
    this.refreshSubmit();
    if (!this.isVisible) {
      this.isVisible = true;
      this.element.offsetHeight;
      this.element.classList.add('visible');
    }
  }

  hide(): void {
    if (!this.isVisible) return;
    this.isVisible = false;
    this.element.classList.remove('visible');
  }

  /** Update only the balance (e.g. after a server-pushed balance change). */
  setBalance(stacks: number): void {
    this.balanceStacks = stacks;
    this.balanceEl.textContent = `💵 ${formatStacks(stacks)}`;
    this.renderChips();
    this.refreshSubmit();
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }

  // ---------- Internal ----------

  private setGameType(t: GameType): void {
    if (this.gameType === t) return;
    this.gameType = t;
    this.element.querySelectorAll('[data-mode]').forEach((b) => {
      b.classList.toggle('sdl-crm-active', (b as HTMLElement).dataset.mode === t);
    });
    this.refreshSubmit();
  }

  private setPrivacy(isPrivate: boolean): void {
    if (this.isPrivate === isPrivate) return;
    this.isPrivate = isPrivate;
    this.element.querySelectorAll('[data-priv]').forEach((b) => {
      const matches = (b as HTMLElement).dataset.priv === (isPrivate ? 'private' : 'public');
      b.classList.toggle('sdl-crm-active', matches);
    });
    const hint = this.element.querySelector('[data-priv-hint]') as HTMLElement;
    hint.textContent = isPrivate
      ? 'Hidden from the public list. Invite friends from inside the game.'
      : 'Listed in the public room list.';
  }

  private renderChips(): void {
    const row = this.element.querySelector('[data-buyin-row]') as HTMLElement;
    row.innerHTML = '';
    BUY_IN_LADDER.forEach((opt) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sdl-crm-chip';
      btn.dataset.buyin = String(opt.stacks);
      btn.textContent = opt.label;
      const affordable = opt.stacks <= this.balanceStacks;
      if (!affordable) btn.classList.add('sdl-crm-disabled');
      if (opt.stacks === this.buyIn && affordable) btn.classList.add('sdl-crm-active');
      btn.addEventListener('click', () => {
        if (!affordable) return;
        this.buyIn = opt.stacks;
        this.renderChips();
        this.refreshSubmit();
      });
      row.appendChild(btn);
    });

    // If current selection is no longer affordable, fall back to the
    // largest affordable amount.
    if (this.buyIn > this.balanceStacks) {
      const fallback = BUY_IN_LADDER.filter((o) => o.stacks <= this.balanceStacks).pop();
      if (fallback) {
        this.buyIn = fallback.stacks;
        this.renderChips();
      }
    }
  }

  private refreshSubmit(): void {
    const ok = (this.gameType === 'ceelo' || this.gameType === 'craps')
      && this.buyIn > 0
      && this.buyIn <= this.balanceStacks;
    this.submitEl.disabled = !ok;
    this.submitEl.classList.toggle('sdl-crm-disabled', !ok);
  }

  private fireSubmit(): void {
    if (this.submitEl.disabled) return;
    this.emit('submit', {
      gameType: this.gameType,
      isPrivate: this.isPrivate,
      buyIn: this.buyIn,
    });
  }

  private fireClose(): void {
    this.hide();
    this.emit('close', undefined);
  }

  // ---------- Styles ----------

  private static injectStyles(): void {
    if (document.getElementById('sdl-crm-styles')) return;
    const style = document.createElement('style');
    style.id = 'sdl-crm-styles';
    style.textContent = `
      .sdl-crm-root {
        position: fixed; inset: 0;
        z-index: var(--z-modal, 501);
        pointer-events: none;
      }
      .sdl-crm-root.visible { pointer-events: auto; }
      .sdl-crm-backdrop {
        position: absolute; inset: 0;
        background: rgba(0, 0, 0, 0.6);
        opacity: 0;
        transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }
      .sdl-crm-root.visible .sdl-crm-backdrop { opacity: 1; }

      .sdl-crm-sheet {
        position: absolute;
        left: 0; right: 0; bottom: 0;
        max-width: 480px;
        margin: 0 auto;
        max-height: 85vh;
        background: var(--dark-bg);
        border-top: 1px solid var(--glass-border);
        border-radius: 20px 20px 0 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        transform: translateY(100%);
        transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 -12px 40px rgba(0, 0, 0, 0.6);
      }
      .sdl-crm-root.visible .sdl-crm-sheet { transform: translateY(0); }

      .sdl-crm-header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: calc(env(safe-area-inset-top) + 14px) 16px 14px;
        border-bottom: 1px solid var(--glass-border);
        flex-shrink: 0;
        background: linear-gradient(180deg, rgba(0,0,0,0.4), transparent);
      }
      .sdl-crm-title-block { flex: 1; min-width: 0; }
      .sdl-crm-crumb {
        font-family: var(--font-body);
        font-size: 10px;
        letter-spacing: 2.5px;
        color: rgba(255,255,255,0.4);
        font-weight: 600;
      }
      .sdl-crm-title {
        font-family: var(--font-display);
        font-size: 22px;
        letter-spacing: 3px;
        line-height: 1;
        background: linear-gradient(180deg, #fff, #c8c8d0);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }
      .sdl-crm-close {
        all: unset;
        width: 36px; height: 36px;
        display: flex; align-items: center; justify-content: center;
        background: var(--concrete-light);
        border: 1px solid var(--glass-border);
        border-radius: 10px;
        color: white;
        font-size: 14px;
        cursor: pointer;
        transition: transform 0.15s ease, background 0.15s ease;
        flex-shrink: 0;
      }
      .sdl-crm-close:active {
        transform: scale(0.92);
        background: var(--concrete-lighter);
      }

      .sdl-crm-body {
        flex: 1;
        overflow-y: auto;
        padding: 16px;
        -webkit-overflow-scrolling: touch;
      }
      .sdl-crm-body::-webkit-scrollbar { width: 0; }

      .sdl-crm-section { margin-bottom: 22px; }
      .sdl-crm-section-label {
        font-family: var(--font-display);
        font-size: 12px;
        letter-spacing: 3px;
        color: rgba(255,255,255,0.55);
        margin-bottom: 10px;
      }
      .sdl-crm-row {
        display: flex;
        gap: 10px;
      }

      /* Game-type cards */
      .sdl-crm-card {
        all: unset;
        flex: 1;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        padding: 16px 12px;
        background: var(--concrete-light);
        border: 1px solid var(--glass-border);
        border-radius: 14px;
        cursor: pointer;
        transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;
        text-align: center;
        box-sizing: border-box;
      }
      .sdl-crm-card:active { transform: scale(0.97); }
      .sdl-crm-card-icon { font-size: 28px; line-height: 1; }
      .sdl-crm-card-title {
        font-family: var(--font-display);
        font-size: 16px;
        letter-spacing: 2px;
        color: white;
        line-height: 1;
      }
      .sdl-crm-card-lock {
        position: absolute;
        top: 6px; right: 6px;
        font-size: 9px;
        font-weight: 700;
        letter-spacing: 1.2px;
        padding: 2px 6px;
        border-radius: 4px;
        background: rgba(255,255,255,0.06);
        color: rgba(255,255,255,0.5);
        border: 1px solid rgba(255,255,255,0.1);
      }
      .sdl-crm-card.sdl-crm-active {
        border-color: var(--gold);
        background: linear-gradient(135deg, rgba(255,215,0,0.18), rgba(255,215,0,0.04));
      }
      .sdl-crm-card.sdl-crm-active .sdl-crm-card-title { color: var(--gold); }
      .sdl-crm-card.sdl-crm-disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }

      /* Privacy pills */
      .sdl-crm-pill {
        all: unset;
        flex: 1;
        text-align: center;
        padding: 10px 0;
        font-family: var(--font-display);
        font-size: 14px;
        letter-spacing: 2px;
        color: rgba(255,255,255,0.6);
        background: var(--concrete-light);
        border: 1px solid var(--glass-border);
        border-radius: 12px;
        cursor: pointer;
        transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;
      }
      .sdl-crm-pill:active { transform: scale(0.97); }
      .sdl-crm-pill.sdl-crm-active {
        border-color: var(--gold);
        background: linear-gradient(135deg, rgba(255,215,0,0.18), rgba(255,215,0,0.04));
        color: var(--gold);
      }
      .sdl-crm-hint {
        margin-top: 8px;
        font-family: var(--font-body);
        font-size: 11px;
        font-weight: 500;
        color: rgba(255,255,255,0.45);
      }

      /* Buy-in chips */
      .sdl-crm-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }
      .sdl-crm-chip {
        all: unset;
        padding: 8px 14px;
        font-family: var(--font-display);
        font-size: 14px;
        letter-spacing: 1.5px;
        color: rgba(255,255,255,0.7);
        background: var(--concrete-light);
        border: 1px solid var(--glass-border);
        border-radius: 999px;
        cursor: pointer;
        transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;
      }
      .sdl-crm-chip:active { transform: scale(0.94); }
      .sdl-crm-chip.sdl-crm-active {
        background: linear-gradient(135deg, var(--gold), var(--gold-dim));
        border-color: var(--gold-light);
        color: #1a1300;
        box-shadow: 0 4px 14px rgba(255,215,0,0.22), inset 0 1px 0 rgba(255,255,255,0.3);
      }
      .sdl-crm-chip.sdl-crm-disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
      .sdl-crm-balance {
        margin-top: 12px;
        font-family: var(--font-body);
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 1.2px;
        color: rgba(255,255,255,0.45);
      }
      .sdl-crm-balance-value {
        color: var(--green);
        font-weight: 700;
      }

      /* Footer */
      .sdl-crm-footer {
        flex-shrink: 0;
        padding: 12px 16px calc(env(safe-area-inset-bottom) + 16px);
        border-top: 1px solid var(--glass-border);
        background: rgba(8,8,10,0.5);
      }
      .sdl-crm-submit {
        all: unset;
        position: relative;
        overflow: hidden;
        width: 100%;
        box-sizing: border-box;
        padding: 14px 18px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: linear-gradient(135deg, var(--gold), var(--gold-dim));
        border: 1px solid var(--gold-light);
        border-radius: 14px;
        font-family: var(--font-display);
        font-size: 18px;
        letter-spacing: 2.5px;
        color: #1a1300;
        cursor: pointer;
        box-shadow: 0 8px 24px rgba(255,215,0,0.18), inset 0 1px 0 rgba(255,255,255,0.3);
        transition: transform 0.15s ease;
      }
      .sdl-crm-submit:active { transform: scale(0.98); }
      .sdl-crm-submit::after {
        content: '';
        position: absolute;
        top: 0; left: -100%;
        width: 60%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
        animation: sdl-crm-shine 3s ease-in-out infinite;
        pointer-events: none;
      }
      /* Pause continuous animations when the modal isn't visible. The modal
         lives on document.body (outside .sdl-ui), so the in-game display:none
         optimization doesn't reach it. Without this, the gold-shine keyframe
         repaints every frame during gameplay. */
      .sdl-crm-root:not(.visible) .sdl-crm-submit::after {
        animation-play-state: paused;
      }
      @keyframes sdl-crm-shine {
        0%, 100% { left: -100%; }
        50% { left: 130%; }
      }
      .sdl-crm-submit-arrow { font-size: 22px; }
      .sdl-crm-submit.sdl-crm-disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
      .sdl-crm-submit.sdl-crm-disabled::after {
        animation: none;
      }
    `;
    document.head.appendChild(style);
  }
}

function formatStacks(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(value % 1_000_000_000 ? 1 : 0)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value % 1_000_000 ? 1 : 0)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
  return `${value}`;
}
